import { useEffect, useRef, useState } from 'react'
import { Plus, Upload, Download, X, Trash2, Clock } from 'lucide-react'
import { useSupabaseTestspielDatesStore } from '../stores/supabaseTestspielDatesStore'

// Termin-Manager für Testspiel-Slots (nur Admins — die Store-Writes prüfen die
// Rolle serverseitig nochmal). Lebt auf der Testspiele-Seite, nicht im Admin:
// wer Testspiele plant, pflegt die Termine an Ort und Stelle.
//
// Gleiches Bedienkonzept wie /admin/karten: Einzel-Add ODER CSV-Upload, der
// editierbare Entwurfszeilen vorbefüllt; erst "Alle speichern" schreibt.
// "Download" exportiert den kompletten Bestand (inkl. vergangener Termine —
// get_all_slots liefert seit Migration 006 alle).

const TIMES = ['10:00', '12:30', '15:00', '17:30', '20:00']

const inputStyle = {
  padding: '9px 12px',
  border: '1px solid #d1d5db',
  borderRadius: 8,
  fontSize: 14,
  fontFamily: 'inherit',
  background: '#fff',
} as const

const smallBtn = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '8px 12px',
  background: '#fff',
  color: '#374151',
  border: '1px solid #d1d5db',
  borderRadius: 8,
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
} as const

interface DraftSlot {
  date: string
  time: string
  notes: string
}

// CSV: eine Zeile = Datum;Zeit;Notiz — Datum als 2026-08-15 oder 15.08.2026,
// Trennzeichen ; , oder Tab, Kopfzeile wird erkannt und übersprungen.
function parseSlotCsv(text: string): DraftSlot[] {
  return text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean)
    .filter((l, i) => !(i === 0 && /datum|date/i.test(l)))
    .map((l) => {
      const [rawDate = '', rawTime = '', ...rest] = l.split(/[;,\t]/).map((p) => p.trim().replace(/^"|"$/g, ''))
      const m = rawDate.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/)
      const date = m ? `${m[3]}-${m[2].padStart(2, '0')}-${m[1].padStart(2, '0')}` : rawDate
      return { date, time: rawTime.slice(0, 5), notes: rest.join(' ') }
    })
    .filter((r) => /^\d{4}-\d{2}-\d{2}$/.test(r.date))
}

export default function TestspielSlotsManager() {
  const { allSlots, fetchAllSlots, addDateTimeSlot, removeDateTimeSlot, getStatusDisplay } =
    useSupabaseTestspielDatesStore()

  const [single, setSingle] = useState<DraftSlot>({ date: '', time: '15:00', notes: '' })
  const [drafts, setDrafts] = useState<DraftSlot[]>([])
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState<string | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [showPast, setShowPast] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    void fetchAllSlots()
  }, [fetchAllSlots])

  const note = (m: string | null, e: string | null) => {
    setMsg(m)
    setErr(e)
  }

  const addSingle = async () => {
    if (!single.date) return note(null, 'Bitte Datum wählen.')
    setBusy(true)
    const res = await addDateTimeSlot(single.date, single.time, single.notes || undefined)
    setBusy(false)
    if (res.error) note(null, res.error)
    else {
      note(`Termin ${single.date} ${single.time} gespeichert.`, null)
      setSingle({ date: '', time: '15:00', notes: '' })
    }
  }

  const onUpload = async (file: File) => {
    const rows = parseSlotCsv(await file.text())
    if (rows.length === 0) return note(null, 'Keine gültigen Zeilen erkannt. Erwartet: Datum;Zeit;Notiz (z.B. 15.08.2026;15:00;Hauptplatz)')
    setDrafts(rows)
    note(`${rows.length} Zeilen aus ${file.name} übernommen — prüfen und speichern.`, null)
  }

  const saveDrafts = async () => {
    setBusy(true)
    note(null, null)
    const problems: string[] = []
    let ok = 0
    let skipped = 0
    // Duplikat-Schutz: existierende Termine und Doppel innerhalb der CSV
    // überspringen — sonst legt ein zweiter Upload alles nochmal an.
    const seen = new Set(allSlots.map((s) => `${s.slot_date} ${s.slot_time}`))
    for (const r of drafts) {
      const key = `${r.date} ${r.time}`
      if (seen.has(key)) {
        skipped++
        continue
      }
      const res = await addDateTimeSlot(r.date, r.time, r.notes || undefined)
      if (res.error) problems.push(`${r.date} ${r.time}: ${res.error}`)
      else {
        ok++
        seen.add(key)
      }
    }
    setBusy(false)
    const skipNote = skipped ? ` ${skipped} übersprungen (existiert bereits).` : ''
    if (problems.length) note(null, `${ok} gespeichert.${skipNote} Probleme: ${problems.join(' · ')}`)
    else {
      note(`${ok} Termine gespeichert.${skipNote}`, null)
      setDrafts([])
    }
  }

  const download = () => {
    const esc = (v: string | null | undefined) => `"${String(v ?? '').replace(/"/g, '""')}"`
    const rows = [...allSlots]
      .sort((a, b) => (a.slot_date + a.slot_time).localeCompare(b.slot_date + b.slot_time))
      .map((s) => [esc(s.slot_date), esc(s.slot_time), esc(getStatusDisplay(s.slot_status)), esc(s.team_value), esc(s.opponent)].join(';'))
    const csv = ['datum;zeit;status;team;gegner', ...rows].join('\r\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob)
    a.download = `testspiel-termine-${new Date().toISOString().slice(0, 10)}.csv`
    a.click()
    URL.revokeObjectURL(a.href)
  }

  const remove = async (id: string, label: string) => {
    if (!confirm(`Termin ${label} löschen?`)) return
    const res = await removeDateTimeSlot(id)
    note(res.error ? null : `Termin ${label} gelöscht.`, res.error ?? null)
  }

  const today = new Date().toISOString().slice(0, 10)
  const upcoming = allSlots.filter((s) => s.slot_date >= today)
  const past = allSlots.filter((s) => s.slot_date < today).reverse() // neueste zuerst

  const statusColor = (status: string): { bg: string; fg: string } => {
    switch (status) {
      case 'available':
        return { bg: '#dcfce7', fg: '#166534' }
      case 'pending':
        return { bg: '#fef3c7', fg: '#92400e' }
      default:
        return { bg: '#fee2e2', fg: '#991b1b' }
    }
  }

  const slotRow = (s: (typeof allSlots)[number], isPast: boolean) => {
    const c = statusColor(s.slot_status)
    const label = `${s.slot_date} ${String(s.slot_time).slice(0, 5)}`
    return (
      <div key={s.slot_id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, opacity: isPast ? 0.65 : 1 }}>
        <span style={{ fontWeight: 600, color: '#111', fontSize: 14, fontVariantNumeric: 'tabular-nums' }}>{label}</span>
        <span style={{ fontSize: 12, fontWeight: 700, padding: '2px 9px', borderRadius: 999, background: c.bg, color: c.fg }}>
          {getStatusDisplay(s.slot_status)}
        </span>
        {s.team_value && (
          <span style={{ fontSize: 13, color: '#6b7280' }}>
            {s.team_value}
            {s.opponent ? ` vs ${s.opponent}` : ''}
          </span>
        )}
        <span style={{ flex: 1 }} />
        {s.slot_status === 'available' && (
          <button onClick={() => void remove(s.slot_id, label)} title="Löschen" style={{ background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', padding: 4, lineHeight: 0 }}>
            <Trash2 size={15} />
          </button>
        )}
      </div>
    )
  }

  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,.06)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
        <h3 style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 17, fontWeight: 800, color: '#111', margin: 0, flex: 1 }}>
          <Clock size={18} color="#1B7E3A" /> Testspiel-Termine
        </h3>
        <button onClick={() => fileRef.current?.click()} title="CSV laden: eine Zeile = Datum;Zeit;Notiz (z.B. 15.08.2026;15:00;Hauptplatz)" style={smallBtn}>
          <Upload size={14} /> CSV laden
        </button>
        <input
          ref={fileRef}
          type="file"
          accept=".csv,.txt"
          style={{ display: 'none' }}
          onChange={(e) => {
            const f = e.target.files?.[0]
            if (f) void onUpload(f)
            e.target.value = ''
          }}
        />
        <button onClick={download} title="Alle Termine als CSV herunterladen" style={smallBtn}>
          <Download size={14} /> Download
        </button>
      </div>

      {msg && <p style={{ color: '#1B7E3A', fontWeight: 600, margin: '0 0 12px', fontSize: 14 }}>✓ {msg}</p>}
      {err && <p style={{ color: '#c8102e', fontWeight: 600, margin: '0 0 12px', fontSize: 14 }}>{err}</p>}

      {/* Einzel-Add */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: drafts.length ? 14 : 18 }}>
        <input type="date" value={single.date} onChange={(e) => setSingle({ ...single, date: e.target.value })} style={inputStyle} />
        <select value={single.time} onChange={(e) => setSingle({ ...single, time: e.target.value })} style={inputStyle}>
          {TIMES.map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
        <input type="text" value={single.notes} onChange={(e) => setSingle({ ...single, notes: e.target.value })} placeholder="Notiz (optional)" style={{ ...inputStyle, flex: '1 1 160px' }} />
        <button onClick={() => void addSingle()} disabled={busy} style={{ ...smallBtn, background: '#1B7E3A', color: '#fff', border: '1px solid #1B7E3A', opacity: busy ? 0.6 : 1 }}>
          <Plus size={14} /> Hinzufügen
        </button>
      </div>

      {/* CSV-Entwürfe */}
      {drafts.length > 0 && (
        <div style={{ marginBottom: 18, padding: 12, background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 10 }}>
            {drafts.length} Termine aus CSV — prüfen, dann speichern:
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 220, overflowY: 'auto' }}>
            {drafts.map((r, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input type="date" value={r.date} onChange={(e) => setDrafts(drafts.map((x, j) => (j === i ? { ...x, date: e.target.value } : x)))} style={inputStyle} />
                <select value={r.time} onChange={(e) => setDrafts(drafts.map((x, j) => (j === i ? { ...x, time: e.target.value } : x)))} style={inputStyle}>
                  {TIMES.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
                <input type="text" value={r.notes} onChange={(e) => setDrafts(drafts.map((x, j) => (j === i ? { ...x, notes: e.target.value } : x)))} placeholder="Notiz" style={{ ...inputStyle, flex: 1 }} />
                <button onClick={() => setDrafts(drafts.filter((_, j) => j !== i))} title="Zeile entfernen" style={{ background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', padding: 4, lineHeight: 0 }}>
                  <X size={15} />
                </button>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
            <button onClick={() => void saveDrafts()} disabled={busy} style={{ ...smallBtn, background: '#1B7E3A', color: '#fff', border: '1px solid #1B7E3A', cursor: busy ? 'wait' : 'pointer', opacity: busy ? 0.6 : 1 }}>
              <Plus size={14} /> {busy ? 'Speichere…' : `Alle speichern (${drafts.length})`}
            </button>
            <button onClick={() => setDrafts([])} disabled={busy} style={smallBtn}>
              Verwerfen
            </button>
          </div>
        </div>
      )}

      {/* Bestand */}
      <div style={{ fontSize: 14, fontWeight: 700, color: '#111', margin: '0 0 8px' }}>
        Kommende Termine ({upcoming.length})
      </div>
      {upcoming.length === 0 ? (
        <p style={{ color: '#6b7280', fontSize: 13, fontStyle: 'italic', margin: '0 0 12px' }}>Keine kommenden Termine.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>{upcoming.map((s) => slotRow(s, false))}</div>
      )}

      <button onClick={() => setShowPast(!showPast)} style={{ ...smallBtn, border: 'none', padding: '4px 0', color: '#1B7E3A', background: 'none' }}>
        {showPast ? '▾' : '▸'} Vergangene Termine ({past.length})
      </button>
      {showPast && past.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>{past.map((s) => slotRow(s, true))}</div>
      )}
    </div>
  )
}
